<login-page>
    <!-- Centralized Passkey Registration Prompt (single instance) -->
    <passkey-registration-prompt
        ref="passkeyPrompt"
        onSuccess="{handlePasskeyPromptSuccess}"
        onDismiss="{handlePasskeyPromptDismiss}"
        onError="{handlePasskeyPromptError}"
    ></passkey-registration-prompt>

    <!-- Login Page Container.
         Widens to max-w-5xl on lg+ when returning users are present
         AND we're not in the middle of a quick-login flow, so we can
         flank the centered form with a slim users-list sidebar on the
         right. In every other state (no returning users, or a returning
         user has been selected and the form expanded) we stay at the
         narrower max-w-md so the centered card doesn't float in a wide
         blank frame. -->
    <div if="{locale}" class="min-h-screen flex items-center rounded-2xl justify-center bg-gradient-to-br from-primary/10 via-base-100 to-secondary/10 p-4">
        <div class="{state.showReturningUsers && !state.quickLoginActive ? 'max-w-5xl' : 'max-w-md'} w-full">
            <!-- Session Expired Alert -->
            <div if="{state.showSessionExpiredMessage}" class="alert alert-warning mb-6 shadow-lg">
                <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4v2m0 0a9 9 0 110-18 9 9 0 010 18z"></path>
                </svg>
                <div>
                    <h3 class="font-bold">{locale?.t('login_session_ended_title')}</h3>
                    <div class="text-sm">{locale?.t('login_session_ended_message')}</div>
                </div>
            </div>

            <!-- EULA Declined Alert -->
            <div if="{state.showEulaDeclinedMessage}" class="alert alert-error mb-6 shadow-lg">
                <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <div>
                    <h3 class="font-bold">{locale?.t('login_eula_required_title')}</h3>
                    <div class="text-sm">{locale?.t('login_eula_required_message')}</div>
                </div>
            </div>

            <!-- Page header.
                 On a branded customer subdomain we show the customer's
                 logo + name large at the top so it stays anchored above
                 the returning-users list. login-form.tag publishes the
                 resolved branding upward via the onBrandingResolved
                 prop — without that hoist, branding lived inside the
                 form card and slid below the fold whenever the user
                 had several remembered accounts. -->
            <div if="{state.customerBranding}" class="text-center mb-8">
                <img if="{state.customerBranding.logoUrl}"
                    src="{state.customerBranding.logoUrl}"
                    alt="{state.customerBranding.companyName}"
                    class="max-h-20 mx-auto mb-4"
                />
                <h1 class="text-3xl font-bold text-primary">{state.customerBranding.companyName}</h1>
                <p class="text-base-content/80 mt-2">{locale?.t('login_signin')}</p>
            </div>
            <div if="{!state.customerBranding}" class="text-center mb-8">
                <img
                    src="/Lark.png"
                    alt="{locale?.t('login_logo_alt')}"
                    class="h-16 mx-auto mb-4"
                />
                <h1 class="text-3xl font-bold text-primary">{locale?.t('login_welcome')}</h1>
                <p class="text-base-content/80 mt-2">{locale?.t('login_signin')}</p>
            </div>

            <!-- Sign-in area.
                 On lg+ screens with returning users present (and the
                 user hasn't selected one yet), we use a 3-column grid
                 with a 1fr spacer on the left, the form anchored to
                 max-w-md in the centre column, and a slim users-list
                 sidebar on the right. The 1fr-spacer pairing keeps
                 the form visually dead-centre on the page (instead
                 of pushed off-axis by the sidebar) — the users list
                 reads as a tacked-on "extra" rather than a co-equal
                 column.

                 On mobile and during a quick-login flow we drop the
                 sidebar layout: the form (or the expanded user card)
                 sits centered in the page's narrower max-w-md
                 container exactly as the no-returning-users case. -->
            <div class="{state.showReturningUsers && !state.quickLoginActive ? 'lg:grid lg:grid-cols-[1fr_minmax(0,28rem)_1fr] lg:gap-6 lg:items-start' : ''}">
                <!-- Left spacer to balance the right-side users sidebar
                     so the centre column (the form) stays horizontally
                     centred on the page. Hidden on mobile. -->
                <div if="{state.showReturningUsers && !state.quickLoginActive}" class="hidden lg:block"></div>

                <!-- Form slot — always centred, max-w-md, regardless of
                     layout mode. -->
                <div class="max-w-md w-full mx-auto">
                    <login-form
                        ref="loginForm"
                        if="{!state.quickLoginActive}"
                        onLoginSuccess="{handleLoginSuccess}"
                        onForgotPassword="{handleForgotPassword}"
                        showPasskeyPrompt="{showPasskeyPrompt}"
                        onConditionalAuthStarted="{handleConditionalAuthStarted}"
                        onConditionalAuthStopped="{handleConditionalAuthStopped}"
                        onBrandingResolved="{handleBrandingResolved}"
                    ></login-form>
                </div>

                <!-- Users-list slot.
                     When the form is showing (the sidebar layout
                     case), the list sits in the right grid column
                     constrained to lg:max-w-xs and styled compact.
                     When a returning user has been picked and the
                     expanded password/passkey form is active, the
                     list takes the centered max-w-md slot and drops
                     compact styling so the expanded card has room to
                     breathe. -->
                <div if="{state.showReturningUsers}" class="{state.quickLoginActive ? 'max-w-md w-full mx-auto mt-6 lg:mt-6' : 'mt-6 lg:mt-0 lg:max-w-xs'}">
                    <returning-users
                        onUserSelected="{handleUserSelected}"
                        onQuickLoginCancelled="{handleQuickLoginCancelled}"
                        onLoginSuccess="{handleReturningUserLoginSuccess}"
                        showPasskeyPrompt="{showPasskeyPrompt}"
                        hideDivider="{true}"
                        compact="{!state.quickLoginActive}"
                    ></returning-users>
                </div>
            </div>

            <!-- Footer Links -->
            <div class="text-center mt-6 text-sm text-base-content/80">
                <p>
                    {locale?.t('login_needhelp')}
                    <a href="mailto:support@metalark.ai" class="link link-primary ms-1">{locale?.t('login_contactsupport')}</a>
                </p>
                <p class="mt-2">
                    <a href="/privacy" class="link link-hover">{locale?.t('login_privacypolicy')}</a>
                    <span class="mx-2">•</span>
                    <a href="/terms" class="link link-hover">{locale?.t('login_termsofservice')}</a>
                </p>
            </div>

            <!-- Powered by MetaLark.
                 Shown only when the customer-branded header is in use,
                 so users always have a visible reminder of the platform
                 behind the login even when the customer's own brand
                 takes the marquee slot at the top. -->
            <div if="{state.customerBranding}" class="text-center mt-4 flex items-center justify-center gap-2 text-xs text-base-content/80">
                <img src="/Lark.png" alt="MetaLark" class="h-4 w-auto opacity-70" />
                <span>{locale?.t('login_poweredby') || 'Powered by MetaLark'}</span>
            </div>

            <!-- Language Selector -->
            <div class="text-center mt-6 flex justify-center">
                <div class="w-64">
                    <language-selector class="flex flex-row"></language-selector>
                </div>
            </div>
        </div>
    </div>

    <script>
        import returningUsers from '../components/auth/returning-users.tag';
        import loginForm from '../components/auth/login-form.tag';
        import passkeyRegistrationPrompt from '../components/auth/passkey-registration-prompt.tag';
        import languageSelector from '../components/language-selector.tag';
        import { ReturningUserManager } from '../services/returning-user-manager.js';

        export default {
            includeTools: false,

            get components() {

                return {
                returningUsers,
                loginForm,
                passkeyRegistrationPrompt,
                languageSelector
            };

            },

            // Note: prop names are lowercase because the router mounts pages
            // via innerHTML, and HTML attribute names get lowercased on parse
            // (`eulaRequired` → `eularequired`). Riot reads props from the
            // lowercased attributes, so multi-word URL params like
            // ?eularequired=true&returnto=/ MUST match here lowercase.
            props: ['reason', 'eularequired', 'returnto'],

            state: {
                showReturningUsers: false,
                quickLoginActive: false,
                showSessionExpiredMessage: false,
                showEulaDeclinedMessage: false,
                conditionalAuthTimeout: null,
                pendingEulaReturnTo: null,
                // Customer branding resolved by login-form's subdomain
                // detection. When set, the page header swaps the MetaLark
                // marquee for the customer's logo + name and we surface a
                // "Powered by MetaLark" line in the footer.
                customerBranding: null
            },

            onBeforeMount() {
                console.log('[LoginPage] onBeforeMount - props:', this.props);
                
                // Check if there are returning users to show
                this.state.showReturningUsers = ReturningUserManager.hasReturningUsers();
                
                // Check localStorage for login message (survives page reload)
                const loginMessage = localStorage.getItem('login-message');
                if (loginMessage === 'eula-declined') {
                    this.state.showEulaDeclinedMessage = true;
                    console.log('[LoginPage] Showing Subscription Agreement declined message from localStorage');
                    // Delay clearing the message to ensure it's displayed even if page remounts
                    // Clear after 5 seconds to give user time to see it
                    setTimeout(() => {
                        localStorage.removeItem('login-message');
                        console.log('[LoginPage] Cleared Subscription Agreement declined message from localStorage');
                    }, 5000);
                }
                
                // Check for reason prop from router (passed via query params)
                if (this.props.reason === 'session-expired') {
                    this.state.showSessionExpiredMessage = true;
                    console.log('[LoginPage] Showing session expired message from prop');
                } else if (this.props.reason === 'eula-declined' && !this.state.showEulaDeclinedMessage) {
                    this.state.showEulaDeclinedMessage = true;
                    console.log('[LoginPage] Showing Subscription Agreement declined message from prop');
                }
            },
            
            onMounted() {
                console.log('[LoginPage] onMounted - props:', this.props);

                // JWT-launch / SSO landing path: backend appends
                // ?eularequired=true&returnto=<path> when the freshly-issued
                // session belongs to a user who hasn't accepted the current
                // EULA. Open the modal immediately and remember where to send
                // the browser after acceptance. (Param names are lowercase —
                // see prop-declaration note above.)
                if (this.props.eularequired === 'true' || this.props.eularequired === true) {
                    this.state.pendingEulaReturnTo = this.props.returnto || '/';
                    console.log('[LoginPage] eularequired=true, pendingEulaReturnTo:', this.state.pendingEulaReturnTo);

                    this.eulaAcceptedHandler = () => {
                        const target = this.state.pendingEulaReturnTo || '/';
                        console.log('[LoginPage] EULA accepted, redirecting to:', target);
                        // Full navigation (not hash-only) so the SPA reboots
                        // with a clean auth/locale init at returnTo.
                        window.location.href = target;
                    };
                    window.addEventListener('eula-accepted', this.eulaAcceptedHandler);

                    // Defer one tick so the global eula-acceptance-modal has
                    // finished mounting before we try to open it.
                    setTimeout(() => {
                        const dialog = document.querySelector('#eula-modal');
                        if (dialog && typeof dialog.showModal === 'function') {
                            console.log('[LoginPage] Opening EULA dialog for JWT-launch flow');
                            dialog.showModal();
                        } else {
                            console.error('[LoginPage] EULA dialog not found — cannot prompt for acceptance');
                        }
                    }, 0);
                }
            },

            onBeforeUnmount() {
                if (this.eulaAcceptedHandler) {
                    window.removeEventListener('eula-accepted', this.eulaAcceptedHandler);
                    this.eulaAcceptedHandler = null;
                }
                this.clearConditionalAuthTimeout();
            },
            
            onUpdated() {
                console.log('[LoginPage] onUpdated - props:', this.props);
                
                // Handle prop changes (when component is already mounted but props change)
                if (this.props.reason === 'session-expired' && !this.state.showSessionExpiredMessage) {
                    this.state.showSessionExpiredMessage = true;
                    console.log('[LoginPage] onUpdated - Showing session expired message from prop');
                    this.update();
                }
            },

            /**
             * Centralized method to show passkey prompt
             * Delegates to PasskeyPromptService for all logic
             *
             * Only shows passkey prompt if user has accepted EULA
             *
             * @param {object} [user] - Pre-fetched /auth/me payload. OAuth
             *     callers should pass the user from loginWithOAuthPopup so we
             *     don't re-fetch /auth/me (and re-trigger /config/session as a
             *     side effect). Password-login callers can omit it — /auth/login
             *     doesn't return eulaAccepted/hasPasskey.
             */
            async showPasskeyPrompt(user) {
                console.log('[LoginPage] showPasskeyPrompt() called');

                try {
                    if (!user) {
                        const { AuthService } = await import('../services/auth-service.js');
                        const userResult = await AuthService.getCurrentUser({ skipLoading: true });

                        if (!userResult.success || !userResult.user) {
                            console.log('[LoginPage] Could not get user info');
                            return false;
                        }
                        user = userResult.user;
                    }

                    if (!user.eulaAccepted) {
                        console.log('[LoginPage] EULA not accepted yet - skipping passkey prompt');
                        return false;
                    }

                    const { PasskeyPromptService } = await import('../services/passkey-prompt-service.js');
                    return await PasskeyPromptService.show(user);
                } catch (error) {
                    console.error('[LoginPage] Error showing passkey prompt:', error);
                    return false;
                }
            },

            /**
             * Start the conditional auth timeout (5 minutes)
             * Automatically dismisses the conditional login flow if it hasn't been completed
             */
            startConditionalAuthTimeout() {
                // Clear any existing timeout
                this.clearConditionalAuthTimeout();
                
                console.log('[LoginPage] Starting conditional auth timeout (5 minutes)');
                
                const FIVE_MINUTES_MS = 5 * 60 * 1000; // 300000 ms
                
                this.state.conditionalAuthTimeout = setTimeout(() => {
                    console.log('[LoginPage] Conditional auth timeout expired - stopping conditional auth');
                    try {
                        if (this.refs && this.refs.loginForm && typeof this.refs.loginForm.stopConditionalAuth === 'function') {
                            console.log('[LoginPage] Stopping conditional auth due to 5-minute timeout');
                            this.refs.loginForm.stopConditionalAuth();
                        } else {
                            console.log('[LoginPage] Login form ref not available for timeout cleanup');
                        }
                    } catch (error) {
                        console.error('[LoginPage] Error stopping conditional auth on timeout:', error.message);
                    }
                }, FIVE_MINUTES_MS);
            },

            /**
             * Clear the conditional auth timeout
             */
            clearConditionalAuthTimeout() {
                if (this.state.conditionalAuthTimeout) {
                    console.log('[LoginPage] Clearing conditional auth timeout');
                    clearTimeout(this.state.conditionalAuthTimeout);
                    this.state.conditionalAuthTimeout = null;
                }
            },

            /**
             * Handle when conditional auth starts (called from login-form)
             * Triggers the 5-minute timeout for challenge expiration
             */
            handleConditionalAuthStarted() {
                console.log('[LoginPage] Conditional auth started - starting 5-minute timeout');
                this.startConditionalAuthTimeout();
            },

            /**
             * Handle when conditional auth stops (called from login-form)
             * Clears the timeout
             */
            handleConditionalAuthStopped() {
                console.log('[LoginPage] Conditional auth stopped - clearing timeout');
                this.clearConditionalAuthTimeout();
            },

            /**
             * Called by login-form once it has resolved the customer
             * subdomain to a branding payload. Lifts the customer's
             * logo + name out of the form card and into the page
             * header so it stays anchored above the returning-users
             * list.
             */
            handleBrandingResolved(branding) {
                if (!branding) return;
                this.state.customerBranding = branding;
                this.update();
            },

            handleUserSelected() {
                this.state.quickLoginActive = true;
                this.update();
            },

            handleQuickLoginCancelled() {
                this.state.quickLoginActive = false;
                this.update();
            },

            handleReturningUserLoginSuccess() {
                console.log('[LoginPage] Returning user login successful');
                // Clear the timeout since login succeeded
                this.clearConditionalAuthTimeout();
                // Try to stop conditional auth if component still exists
                try {
                    if (this.refs && this.refs.loginForm && typeof this.refs.loginForm.stopConditionalAuth === 'function') {
                        console.log('[LoginPage] Stopping conditional auth in login-form');
                        this.refs.loginForm.stopConditionalAuth();
                    } else {
                        console.log('[LoginPage] Login form ref not available (component may be unmounted or hidden)');
                    }
                } catch (error) {
                    console.log('[LoginPage] Could not stop conditional auth:', error.message);
                }
            },

            handleLoginSuccess(user) {
                console.log('[LoginPage] Login successful:', user);
                // Additional success handling if needed
                // Navigation is handled by the login-form component
            },

            handleForgotPassword() {
                console.log('[LoginPage] Forgot password clicked');
                // Navigate to forgot password page
                window.location.href = '/forgot-password';
            },

            // Honor ?returnto=<path> when the user came here from a deep
            // link (e.g. accept-invitation bouncing through login for an
            // unauthenticated visitor). Same validation as login-form's
            // helper: only same-origin relative paths are accepted.
            getPostLoginDestination() {
                const raw = this.props.returnto || '';
                if (!raw) return '/';
                if (!raw.startsWith('/') || raw.startsWith('//')) return '/';
                if (/^[a-z][a-z0-9+.-]*:/i.test(raw)) return '/';
                return raw;
            },

            handlePasskeyPromptSuccess(result) {
                console.log('[LoginPage] Passkey added successfully:', result);
                // Clear the timeout since registration succeeded
                this.clearConditionalAuthTimeout();
                // Navigate to the post-login destination after passkey is added
                window.location.href = this.getPostLoginDestination();
            },

            handlePasskeyPromptDismiss() {
                console.log('[LoginPage] Passkey prompt dismissed');
                // Clear the timeout since prompt was dismissed
                this.clearConditionalAuthTimeout();

                // Try to stop conditional auth if component still exists
                try {
                    if (this.refs && this.refs.loginForm && typeof this.refs.loginForm.stopConditionalAuth === 'function') {
                        console.log('[LoginPage] Stopping conditional auth on dismiss');
                        this.refs.loginForm.stopConditionalAuth();
                    } else {
                        console.log('[LoginPage] Login form ref not available (component may be unmounted)');
                    }
                } catch (error) {
                    console.log('[LoginPage] Could not stop conditional auth:', error.message);
                }

                // Navigate to the post-login destination when user clicks "Maybe Later"
                const dest = this.getPostLoginDestination();
                console.log('[LoginPage] Navigating to', dest);
                window.location.href = dest;
            },

            handlePasskeyPromptError(error) {
                console.error('[LoginPage] Passkey prompt error:', error);
                // Clear the timeout since prompt errored
                this.clearConditionalAuthTimeout();
                // Navigate to the post-login destination even on error
                window.location.href = this.getPostLoginDestination();
            }
        }
    </script>
</login-page>